import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Trash2, ArrowLeft, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';
import ReturnToLiveButton from '@/components/ReturnToLiveButton';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;
const UNSEL_KEY = 'livemoa:cart_unselected';
const loadUnsel = () => { try { const v = JSON.parse(localStorage.getItem(UNSEL_KEY) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; } };

/**
 * Cart page — Phase-1 UI only. The user can add/update/remove items and
 * see the subtotal, but the "주문/결제하기" button is a stub. Once payment
 * providers are wired up we'll hook this button to the checkout flow.
 */
const Cart = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  // LIGHTPAY-PG-PRODUCTION-READY-1 · 선택 해제한 항목만 저장 → 새로 담은 상품은 기본 선택, 선택 상태는 새로고침 후에도 유지.
  const [unsel, setUnsel] = useState(loadUnsel);
  const saveUnsel = (next) => { setUnsel(next); try { localStorage.setItem(UNSEL_KEY, JSON.stringify(next)); } catch {} };

  const load = async () => {
    try {
      const res = await api.get('/cart');
      setCart(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error('로그인이 필요합니다.');
        navigate('/login');
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const changeQty = async (item_id, quantity) => {
    if (quantity < 1) return;
    try { await api.patch(`/cart/items/${item_id}`, { quantity }); load(); window.dispatchEvent(new Event('livemoa:cart-updated')); }
    catch (e) { toast.error(e?.response?.data?.detail || '수량 변경 실패'); }
  };
  const remove = async (item_id) => {
    try { await api.delete(`/cart/items/${item_id}`); load(); window.dispatchEvent(new Event('livemoa:cart-updated')); toast.success('장바구니에서 삭제되었습니다.'); }
    catch (e) { toast.error(e?.response?.data?.detail || '삭제 실패'); }
  };

  const items = cart?.items || [];
  const selected = items.filter((it) => !unsel.includes(it.item_id));
  const allOn = items.length > 0 && selected.length === items.length;
  const toggle = (id) => saveUnsel(unsel.includes(id) ? unsel.filter((x) => x !== id) : [...unsel, id]);
  const toggleAll = () => saveUnsel(allOn ? items.map((it) => it.item_id) : []);
  const removeSelected = async () => {
    if (!selected.length || !window.confirm(`선택한 상품 ${selected.length}개를 장바구니에서 삭제할까요?`)) return;
    try {
      for (const it of selected) await api.delete(`/cart/items/${it.item_id}`); // eslint-disable-line no-await-in-loop
      toast.success('선택한 상품을 삭제했습니다.');
    } catch (e) { toast.error(e?.response?.data?.detail || '삭제 실패'); }
    load(); window.dispatchEvent(new Event('livemoa:cart-updated'));
  };
  const total = selected.reduce((s, it) => s + (Number(it.price_snapshot) || 0) * (Number(it.quantity) || 0), 0);

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] pb-24 sm:pb-8" data-testid="cart-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-4 sm:pt-8">
        <div className="flex items-center justify-between mb-4">
          <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-xs sm:text-sm text-gray-500 hover:text-black dark:hover:text-white">
            <ArrowLeft size={14} /> 뒤로
          </button>
          {/* STEP 8-I-4 · Return-to-LIVE CTA (sessionStorage 에 방송 URL 있으면 노출) */}
          <ReturnToLiveButton />
        </div>
        <div className="flex items-center gap-2 mb-6">
          <ShoppingCart className="text-[#FF3B30]" size={22} />
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>장바구니</h1>
          {cart?.items?.length > 0 && <span className="text-xs text-gray-400" data-testid="cart-count">{cart.items.length}개</span>}
        </div>

        {loading ? (
          <div className="text-sm text-gray-400 py-6">불러오는 중…</div>
        ) : (cart?.items || []).length === 0 ? (
          <div className="py-16 text-center bg-black/[0.02] dark:bg-white/[0.03] rounded-2xl border border-dashed border-black/10 dark:border-white/10" data-testid="cart-empty">
            <ShoppingBag className="mx-auto text-gray-300" size={28} />
            <p className="mt-3 text-sm font-bold">장바구니가 비어 있습니다.</p>
            <Link to="/" className="mt-3 inline-block text-xs font-bold text-[#FF3B30] hover:underline">홈으로 돌아가서 쇼핑하기 →</Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="cart-items">
            <div className="flex items-center justify-between text-sm">
              <label className="inline-flex items-center gap-2 font-bold cursor-pointer">
                <input type="checkbox" checked={allOn} onChange={toggleAll} className="w-4 h-4 accent-[#FF3B30]" data-testid="cart-select-all" />
                전체선택 <span className="text-gray-400 font-normal" data-testid="cart-select-count">({selected.length}/{items.length})</span>
              </label>
              <button type="button" onClick={removeSelected} disabled={!selected.length}
                      className="text-xs font-bold text-gray-500 hover:text-[#FF3B30] disabled:opacity-30" data-testid="cart-remove-selected">선택삭제</button>
            </div>
            {cart.items.map((it) => (
              <div key={it.item_id} className="flex items-center gap-3 p-3 rounded-xl border border-black/10 dark:border-white/10">
                <input type="checkbox" checked={!unsel.includes(it.item_id)} onChange={() => toggle(it.item_id)}
                       className="w-4 h-4 shrink-0 accent-[#FF3B30]" aria-label={`${it.name_snapshot} 선택`} data-testid={`cart-select-${it.item_id}`} />
                <Link to={`/product/${it.product_id}`} className="shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
                  <SafeImg src={it.image_snapshot} kind="product" alt="" className="w-full h-full object-cover" />
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${it.product_id}`} className="font-bold text-sm truncate hover:text-[#FF3B30]">{it.name_snapshot}</Link>
                  {Object.keys(it.option || {}).length > 0 && (
                    <p className="text-[11px] text-gray-500 truncate">{Object.entries(it.option).map(([k, v]) => `${k}: ${v}`).join(' · ')}</p>
                  )}
                  <div className="mt-1 flex items-center gap-2 text-xs">
                    <div className="inline-flex items-center border border-black/10 dark:border-white/10 rounded-full">
                      <button type="button" onClick={() => changeQty(it.item_id, it.quantity - 1)} className="px-2 py-1" aria-label="수량 감소">-</button>
                      <span className="px-2 font-bold">{it.quantity}</span>
                      <button type="button" onClick={() => changeQty(it.item_id, it.quantity + 1)} className="px-2 py-1" aria-label="수량 증가">+</button>
                    </div>
                    <span className="font-black">{won((it.price_snapshot || 0) * (it.quantity || 0))}</span>
                  </div>
                </div>
                <button type="button" onClick={() => remove(it.item_id)} className="p-2 text-[#FF3B30]" aria-label="삭제" data-testid={`cart-remove-${it.item_id}`}><Trash2 size={14} /></button>
              </div>
            ))}
            <div className="pt-4 border-t border-black/5 dark:border-white/10 space-y-1">
              <p className="text-xs text-gray-500" data-testid="cart-selected-summary">선택 상품 {selected.length}개</p>
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-gray-500">상품금액</p>
                <p className="text-2xl font-black" data-testid="cart-subtotal">{won(total)}</p>
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>배송비</span><span data-testid="cart-shipping-note">주문서에서 확인</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {(cart?.items || []).length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 bg-white dark:bg-[#0A0A0A] border-t border-black/10 dark:border-white/10 px-4 py-3 sm:static sm:border-none sm:mt-6 sm:max-w-3xl sm:mx-auto sm:px-6">
          <button type="button" disabled={!selected.length}
            onClick={() => {
              try {
                sessionStorage.removeItem('livemoa:pending_order');
                sessionStorage.setItem('livemoa:cart_selection', JSON.stringify(selected.map((it) => it.item_id)));
              } catch {}
              navigate('/checkout');
            }}
            className="w-full py-3 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-sm font-bold disabled:opacity-40 disabled:hover:bg-[#FF3B30]"
            data-testid="cart-checkout"
          >{selected.length ? `선택 상품 주문하기 (${selected.length})` : '주문할 상품을 선택해주세요'}</button>
        </div>
      )}
    </div>
  );
};

export default Cart;
